import { Draft } from 'immer';
import { create, type StoreApi, type UseBoundStore } from 'zustand';
import {
  createJSONStorage,
  persist,
  type PersistOptions,
  type StateStorage,
} from 'zustand/middleware';
import { immer, type ImmerStateCreator } from 'zustand/middleware/immer';

type StoreMethods<Fields> = {
  set: (updates: Partial<Fields>) => void;
  reset: (fields?: (keyof Fields)[]) => void;
};

export type StoreState<Fields> = Fields & StoreMethods<Fields>;

type BeforeSetHook<Fields> = (
  draft: Draft<StoreState<Fields>>,
  updates: Partial<Fields>,
) => Partial<Fields> | void;

type PersistConfig<Fields> = {
  name: string;
  storage?: StateStorage;
  partialize?: PersistOptions<StoreState<Fields>, Fields>['partialize'];
};

type StoreHooks<Fields> = {
  beforeSet?: BeforeSetHook<Fields>;
};

type StoreOptions<Fields> = {
  persist?: boolean | string | PersistConfig<Fields>;
  persistKey?: string;
  storage?: StateStorage;
  partialize?: PersistConfig<Fields>['partialize'];
  hooks?: StoreHooks<Fields>;
};

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

function resolveStorage(custom?: StateStorage): StateStorage {
  if (custom) {
    return custom;
  }
  return createJSONStorage(() => (typeof window === 'undefined' ? noopStorage : window.localStorage));
}

function resolvePersistConfig<Fields extends Record<string, unknown>>(
  defaults: Fields,
  options: StoreOptions<Fields>,
): PersistOptions<StoreState<Fields>, Fields> | undefined {
  const { persist: persistOption } = options;
  if (!persistOption) {
    return undefined;
  }

  const keys = Object.keys(defaults) as (keyof Fields)[];
  const baseState = { ...defaults };

  const partialize =
    options.partialize ??
    (typeof persistOption === 'object' && 'partialize' in persistOption
      ? persistOption.partialize
      : undefined) ??
    ((state: StoreState<Fields>) => {
      const snapshot = { ...baseState };
      keys.forEach((key) => {
        snapshot[key] = state[key];
      });
      return snapshot;
    });

  const storage =
    options.storage ??
    (typeof persistOption === 'object' && 'storage' in persistOption
      ? persistOption.storage
      : undefined);

  if (typeof persistOption === 'string') {
    return {
      name: persistOption,
      storage: resolveStorage(storage),
      partialize,
    };
  }

  if (persistOption === true) {
    const name = options.persistKey;
    if (!name) {
      throw new Error('createStore: persist flag set to true but no persistKey provided.');
    }
    return {
      name,
      storage: resolveStorage(storage),
      partialize,
    };
  }

  return {
    name: persistOption.name,
    storage: resolveStorage(storage),
    partialize,
  };
}

export function createStore<Fields extends Record<string, unknown>>(
  defaults: Fields,
  options: StoreOptions<Fields> = {},
): UseBoundStore<StoreApi<StoreState<Fields>>> {
  type FieldsKey = keyof Fields;

  const keys = Object.keys(defaults) as FieldsKey[];
  const baseState = { ...defaults };

  const creator: ImmerStateCreator<StoreState<Fields>> = immer((set) => {
    const setFields: StoreMethods<Fields>['set'] = (updates) =>
      set((draft) => {
        const nextUpdates = options.hooks?.beforeSet?.(draft, updates) ?? updates;
        Object.assign(draft, nextUpdates);
      });

    const resetFields: StoreMethods<Fields>['reset'] = (fields) =>
      set((draft) => {
        (fields ?? keys).forEach((key) => {
          draft[key] = baseState[key];
        });
      });

    return {
      ...baseState,
      set: setFields,
      reset: resetFields,
    };
  });

  const persistConfig = resolvePersistConfig(baseState, options);
  if (!persistConfig) {
    return create<StoreState<Fields>>()(creator);
  }

  return create<StoreState<Fields>>()(persist(creator, persistConfig));
}

export type { StoreHooks, StoreMethods };
